'use client';

import { useEffect, useState } from 'react';
import { isBrowser, getOperatingSystem, getBrowser, OPERATING_SYSTEM, BROWSER } from 'utils';
import { SCREEN_SIZES } from '../../constants';

interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  operatingSystem: string;
  browser: string;
}

/**
 * Custom React hook to determine the current device type and operating system.
 *
 * It detects whether the user's device falls into one of three categories based on screen width:
 * - Mobile: width < SCREEN_SIZES.tablet
 * - Tablet: SCREEN_SIZES.tablet ≤ width < SCREEN_SIZES.desktop
 * - Desktop: width ≥ SCREEN_SIZES.desktop
 *
 * It also detects the user's operating system using the `getOperatingSystem` utility.
 *
 * The hook listens to window resize events and updates the device type reactively.
 *
 * @returns {Object} An object containing:
 * - `isMobile`: `true` if the device is mobile
 * - `isTablet`: `true` if the device is a tablet
 * - `isDesktop`: `true` if the device is a desktop
 * - `operatingSystem`: A string representing the detected OS (e.g., 'iOS', 'Android', 'Windows', etc.)
 * - `browser`: A string representing the detected browser (e.g., 'Chrome', 'Firefox', 'Safari', etc.)
 *
 */
export function useDeviceInfo(): DeviceInfo {
  const getDevice = (): Omit<DeviceInfo, 'operatingSystem' | 'browser'> => {
    if (!isBrowser()) {
      // Default to mobile when rendering server-side
      return {
        isMobile: true,
        isTablet: false,
        isDesktop: false,
      };
    }

    const width = window.innerWidth;

    return {
      isMobile: width < SCREEN_SIZES.tablet,
      isTablet: width >= SCREEN_SIZES.tablet && width < SCREEN_SIZES.desktop,
      isDesktop: width >= SCREEN_SIZES.desktop,
    };
  };

  const [device, setDevice] = useState(getDevice);
  const [operatingSystem] = useState<string>(() => (isBrowser() ? getOperatingSystem() : OPERATING_SYSTEM.UNKNOWN));
  const [browser] = useState<string>(() => (isBrowser() ? getBrowser() : BROWSER.UNKNOWN));

  useEffect(() => {
    if (!isBrowser()) return;

    const handleResize = () => {
      setDevice(getDevice());
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    ...device,
    operatingSystem,
    browser,
  };
}
